import React, { useEffect, useState } from 'react';
import './VehicleVerificationInput.scss';
import PinInput from 'react-pin-input';
import { useTranslation } from 'react-i18next';
import { Container, ContainerGutter, Text, TokenTextAppearance, TextTag } from '@volkswagen-onehub/components-core';
import { Checkmark } from '@volkswagen-onehub/icons-core';
import { UserAuthenticationData } from '../../models/UserAuthenticationData';
import { PreFilledAuthData } from '../../models/TechnicalData';

function VehicleVerificationInput(props: InputProps) {
	const { preFilledAuthData } = props;
	const { t } = useTranslation('ns1', { useSuspense: false });

	const [pknComplete, setPknComplete] = useState(false);
	const [variantComplete, setVariantComplete] = useState(false);
	const [versionComplete, setVersionComplete] = useState(false);
	const [variantInput, setVariantInput] = useState<PinInput | null>(null);
	const [versionInput, setVersionInput] = useState<PinInput | null>(null);

	useEffect(() => {
		if (pknComplete && variantComplete && versionComplete) {
			props.setIsDataComplete(true);
		} else {
			props.setIsDataComplete(false);
		}
	}, [pknComplete, props, variantComplete, versionComplete]);

	return (
		<>
			<Container wrap="always" gutter={ContainerGutter.dynamic0100}>
				<div style={{ paddingRight: '10px' }}>
					<div className="flex-center-spaced">
						<Text appearance={TokenTextAppearance.copy200}>{t('vehicleVerification.secondSection.pkn')}</Text>

						{pknComplete && (
							<div className="green-checkbox-filled-title">
								<Checkmark />
							</div>
						)}
					</div>

					<div className="flex-center-spaced">
						<div style={{ display: 'flex', alignItems: 'center' }}>
							<PinInput
								length={7}
								initialValue=""
								ariaLabel={t('vehicleVerification.secondSection.pkn')}
								onChange={(value) => {
									if (value.length !== 7) {
										setPknComplete(false);
									}
								}}
								type="custom"
								inputMode="number"
								style={{ fontSize: 'var(--textappearances-copy250-fontsize)', marginTop: '8px', marginBottom: '8px' }}
								inputStyle={{ borderColor: 'lightgray', borderRadius: '4px', height: '32px', width: '32px' }}
								inputFocusStyle={{ borderColor: '#00B0F0' }}
								onComplete={(value) => {
									props.setUserAuthenticationData({ ...props.userAuthenticationData, ...{ pkn: value } });
									setPknComplete(true);
									if (variantInput) {
										variantInput.focus();
									}
								}}
								autoSelect
								regexCriteria={/^[ A-Za-z0-9_@./#&+-]*$/}
								focus
							/>
						</div>

						{pknComplete && (
							<div className="green-checkbox-filled">
								<Checkmark />
							</div>
						)}
					</div>
				</div>

				<div className="complete-field">
					<div className="left-field">
						<Text tag={TextTag.span} appearance={TokenTextAppearance.bigcopy100}>
							D.2
						</Text>
					</div>

					<div className="right-field">
						<div style={{ padding: '8px' }}>
							<Text appearance={TokenTextAppearance.copy200}>{t('vehicleVerification.secondSection.type')}</Text>

							<div className="flex-center-spaced">
								<PinInput
									length={preFilledAuthData.type.length}
									initialValue={preFilledAuthData.type}
									ariaLabel={t('vehicleVerification.secondSection.type')}
									type="custom"
									inputMode="number"
									style={{ fontSize: 'var(--textappearances-copy250-fontsize)', marginTop: '8px', marginBottom: '8px' }}
									inputStyle={{ borderColor: 'whitesmoke', borderRadius: '4px', height: '32px', width: '32px', background: 'whitesmoke' }}
									disabled
								/>
							</div>
						</div>

						<div style={{ padding: '8px' }}>
							<div className="flex-center-spaced">
								<Text appearance={TokenTextAppearance.copy200}>{t('vehicleVerification.secondSection.variant')}</Text>

								{variantComplete && (
									<div className="green-checkbox-filled-title">
										<Checkmark />
									</div>
								)}
							</div>

							<div className="flex-center-spaced">
								<div style={{ display: 'flex', alignItems: 'center' }}>
									<PinInput
										length={2}
										initialValue={preFilledAuthData.variant}
										ariaLabel={t('vehicleVerification.secondSection.variant')}
										type="custom"
										inputMode="number"
										style={{ fontSize: 'var(--textappearances-copy250-fontsize)', marginTop: '8px', marginBottom: '8px' }}
										inputStyle={{ borderColor: 'whitesmoke', borderRadius: '4px', height: '32px', width: '32px', background: 'whitesmoke' }}
										disabled
									/>

									<PinInput
										length={4}
										initialValue=""
										ariaLabel={t('vehicleVerification.secondSection.variant')}
										onChange={(value) => {
											if (value.length !== 4) {
												setVariantComplete(false);
											}
										}}
										type="custom"
										inputMode="number"
										style={{ fontSize: 'var(--textappearances-copy250-fontsize)', marginTop: '8px', marginBottom: '8px' }}
										inputStyle={{ borderColor: 'lightgray', borderRadius: '4px', height: '32px', width: '32px' }}
										inputFocusStyle={{ borderColor: '#00B0F0' }}
										onComplete={(value) => {
											props.setUserAuthenticationData({ ...props.userAuthenticationData, ...{ variant: value } });
											setVariantComplete(true);
											if (versionInput) {
												versionInput.focus();
											}
										}}
										autoSelect
										regexCriteria={/^[ A-Za-z0-9_@./#&+-]*$/}
										ref={(element) => {
											setVariantInput(element);
										}}
									/>

									<PinInput
										length={2}
										initialValue="XX"
										ariaLabel={t('vehicleVerification.secondSection.variant')}
										type="custom"
										inputMode="number"
										style={{ fontSize: 'var(--textappearances-copy250-fontsize)', marginTop: '8px', marginBottom: '8px' }}
										inputStyle={{
											borderColor: 'whitesmoke',
											borderRadius: '4px',
											height: '32px',
											width: '32px',
											background: 'whitesmoke',
											color: 'darkgray',
										}}
										disabled
									/>
								</div>

								{variantComplete && (
									<div className="green-checkbox-filled">
										<Checkmark />
									</div>
								)}
							</div>
						</div>

						<div style={{ padding: '8px' }}>
							<div className="flex-center-spaced">
								<Text appearance={TokenTextAppearance.copy200}>{t('vehicleVerification.secondSection.version')}</Text>

								{versionComplete && (
									<div className="green-checkbox-filled-title">
										<Checkmark />
									</div>
								)}
							</div>

							<div className="flex-center-spaced">
								<div style={{ display: 'flex', alignItems: 'center' }}>
									<PinInput
										length={2}
										initialValue={preFilledAuthData.version}
										ariaLabel={t('vehicleVerification.secondSection.version')}
										type="custom"
										inputMode="number"
										style={{ fontSize: 'var(--textappearances-copy250-fontsize)', marginTop: '8px', marginBottom: '8px' }}
										inputStyle={{ borderColor: 'whitesmoke', borderRadius: '4px', height: '32px', width: '32px', background: 'whitesmoke' }}
										disabled
									/>

									<PinInput
										length={6}
										initialValue=""
										ariaLabel={t('vehicleVerification.secondSection.version')}
										onChange={(value) => {
											if (value.length !== 6) {
												setVersionComplete(false);
											}
										}}
										type="custom"
										inputMode="number"
										style={{ fontSize: 'var(--textappearances-copy250-fontsize)', marginTop: '8px', marginBottom: '8px' }}
										inputStyle={{ borderColor: 'lightgray', borderRadius: '4px', height: '32px', width: '32px' }}
										inputFocusStyle={{ borderColor: '#00B0F0' }}
										onComplete={(value) => {
											props.setUserAuthenticationData({ ...props.userAuthenticationData, ...{ version: value } });
											setVersionComplete(true);
										}}
										autoSelect
										regexCriteria={/^[ A-Za-z0-9_@./#&+-]*$/}
										ref={(element) => {
											setVersionInput(element);
										}}
									/>

									<div className="subsequent-xs">
										<PinInput
											length={3}
											initialValue="XXX"
											ariaLabel={t('vehicleVerification.secondSection.version')}
											type="custom"
											inputMode="number"
											style={{ fontSize: 'var(--textappearances-copy250-fontsize)', marginTop: '8px', marginBottom: '8px' }}
											inputStyle={{
												borderColor: 'whitesmoke',
												borderRadius: '4px',
												height: '32px',
												width: '32px',
												background: 'whitesmoke',
												color: 'darkgray',
											}}
											disabled
										/>
									</div>
								</div>

								{versionComplete && (
									<div className="green-checkbox-filled">
										<Checkmark />
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<Text appearance={TokenTextAppearance.copy200}>{t('vehicleVerification.secondSection.legalNotice')}</Text>
			</Container>
		</>
	);
}

interface InputProps {
	setIsDataComplete: React.Dispatch<React.SetStateAction<boolean>>;
	userAuthenticationData: UserAuthenticationData;
	setUserAuthenticationData: React.Dispatch<React.SetStateAction<UserAuthenticationData>>;
	preFilledAuthData: PreFilledAuthData;
}

export default VehicleVerificationInput;
